import Select from '../Select/Select';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import almaLogo from '../../../assets/image/alma-linux.svg';
import centosLogo from '../../../assets/image/centos.svg';
import debianLogo from '../../../assets/image/debian.svg';
import rockyLogo from '../../../assets/image/rocky-linux.svg';
import ubuntuLogo from '../../../assets/image/ubuntu.svg';
import windowsLogo from '../../../assets/image/windows-server.svg';

export interface OperationSystemOption {
    name: string;
    slug: string;
    versions: Array<{
        name: string;
        slug: string;
    }>;
}

interface OperationSystemOptionListItemProps {
    os: OperationSystemOption;
    /** Radio group name shared by every item of one list. */
    name: string;
    isSelected?: boolean;
    onClick?: () => void;
    selectedVersionSlug?: string | null;
    onSelectVersion?: (versionSlug: string | null) => void;
    versionPlaceholder?: string;
}

export default function OperationSystemOptionListItem({
    os,
    name,
    isSelected = false,
    onClick,
    selectedVersionSlug,
    onSelectVersion,
    versionPlaceholder,
}: OperationSystemOptionListItemProps) {
    const getLogoPath = (osName: string): string => {
        const lowerName = osName.toLowerCase();

        if (lowerName.includes('windows')) {
            return windowsLogo;
        }

        const nameMap: Record<string, string> = {
            ubuntu: ubuntuLogo,
            debian: debianLogo,
            'alma linux': almaLogo,
            almalinux: almaLogo,
            fedora: centosLogo,
            rocky: rockyLogo,
            'rocky linux': rockyLogo,
            arch: centosLogo,
            centos: centosLogo,
        };

        const logo = nameMap[lowerName];
        if (logo) return logo;

        return ubuntuLogo;
    };

    const { t } = useTranslation('common');
    const logoPath = getLogoPath(os.name);
    const radioId = useId();

    return (
        <div
            className={`
                relative h-auto p-3 rounded-md border-[0.5px] transition-all cursor-pointer
                ${
                    isSelected
                        ? 'bg-(--color-selected-bg) border-blue-500'
                        : 'bg-surface-1 border-line hover:border-line-strong'
                }
            `}
        >
            <div className="flex flex-col justify-center items-center gap-3 w-full">
                <div className="w-12 h-12 flex items-center justify-center overflow-hidden">
                    <img src={logoPath} alt={os.name} className="w-full h-full object-contain" />
                </div>
                <div className="flex flex-col justify-center items-center w-full">
                    <h3 className="text-base font-semibold">
                        <input
                            id={radioId}
                            type="radio"
                            name={name}
                            className="peer sr-only"
                            checked={isSelected}
                            onChange={onClick}
                        />
                        <label
                            htmlFor={radioId}
                            className="cursor-pointer after:absolute after:inset-0 after:rounded-md peer-focus-visible:after:outline-2 peer-focus-visible:after:outline-offset-2 peer-focus-visible:after:outline-fg"
                        >
                            {os.name}
                        </label>
                    </h3>
                    <p className="text-[14px] text-fg/68">
                        {os.versions && os.versions.length > 0
                            ? t('operatingSystem.versionCount', { count: os.versions.length })
                            : t('operatingSystem.noVersions')}
                    </p>
                    {os.versions && os.versions.length > 0 && (
                        <div className="relative w-full mt-2">
                            <Select
                                size="xs"
                                aria-label={t('operatingSystem.versionLabel', { os: os.name })}
                                placeholder={versionPlaceholder}
                                value={selectedVersionSlug ?? null}
                                onChange={onSelectVersion}
                                data={os.versions.map((version) => ({
                                    value: version.slug,
                                    label: version.name,
                                }))}
                                styles={{
                                    input: {
                                        backgroundColor: 'var(--surface-2)',
                                        color: 'var(--fg)',
                                    },
                                    option: {
                                        backgroundColor: 'var(--surface-2)',
                                        color: 'var(--fg)',
                                    },
                                    dropdown: {
                                        backgroundColor: 'var(--surface-2)',
                                        borderColor: 'var(--line)',
                                    },
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
